import { OrderedMap, Map, Set } from 'immutable'

import { GRID_DATES_MODAL } from 'constants/modal-constants'
import actions from 'actions'
import { getDateKey, getTimeHour, getTimeMinute } from 'utils/datetime'

import { DateRecord } from './grid-records'
import {
  createDeal,
  createSellableItem,
  getMostCommonQuantity,
  getOrCreateDate,
  getOrCreateTime,
  recomputeGrid,
} from './grid-functions'

export const defaultState = {
  dates: new OrderedMap(),  // dateId -> DateRecord
  times: new OrderedMap(),  // timeId -> TimeRecord
  sellableItems: new OrderedMap(),  // sellableItemId -> SellableItemRecord
  deals: new Map(),  // dealId -> DealRecord
  datesToTimesToDeals: new Map(),  // dateId -> timeId -> set(dealId's)

  datePickerDates: new OrderedMap(),
  gridRecomputeTime: 0,
  nextColorIndex: 0,
  isLoading: true,
  isSaving: false,
  currentEditingSellableItemId: null,
  operationError: null,
}

export default function grid(state = defaultState, action) {
  switch (action.type) {
    case actions.COMMON_SHOW_MODAL: {
      // When modal is shown for the date picker, copy the dates
      if (action.modalName === GRID_DATES_MODAL) {
        return {
          ...state,
          datePickerDates: state.dates,
        }
      }
      return state
    }

    case actions.GRID_LOAD_EVENT_GROUP: {
      return defaultState  // load default state when loading new event group
    }

    case actions.GRID_LOAD_EVENT_GROUP_DATA: {
      const { deals, sellableItems } = action

      // Load sellable items
      let newState = state
      sellableItems.forEach(sellableItem => {
        newState = createSellableItem(sellableItem)(newState)
      })

      // Load deals
      deals.forEach(deal => {
        // Get or create date
        const dateKey = getDateKey(deal.startTime)
        const { updatedDateState, date } = getOrCreateDate(dateKey)(newState)
        newState = updatedDateState

        // Get or create time
        const hour = getTimeHour(deal.startTime)
        const minute = getTimeMinute(deal.startTime)
        const { updatedTimeState, time } = getOrCreateTime(hour, minute)(newState)
        newState = updatedTimeState

        const sellableItemRecord = newState.sellableItems.find(
          sellableItem => sellableItem.serverId === deal.sellableItem
        )
        newState = createDeal(date.id, time.id, sellableItemRecord.id, deal)(newState)
      })

      // Set default quantity on sellable items
      return {
        ...newState,
        sellableItems: newState.sellableItems.map(sellableItem =>
          sellableItem.set('quantity', getMostCommonQuantity(sellableItem.id)(newState))
        ),
        isLoading: false,
        isSaving: false,
      }
    }

    case actions.GRID_DATE_PICKER_SELECT_DATES: {
      // filter out unselected dates
      let datePickerDates = state.datePickerDates.filter(date => action.dates.indexOf(date.date) !== -1)

      // find new dates
      const existingDates = new Set(datePickerDates.toArray().map(date => date.date))
      const newDates = new Set(action.dates).subtract(existingDates)

      // create new dates
      newDates.forEach(date => {
        const newDate = new DateRecord(date)
        datePickerDates = datePickerDates.set(newDate.id, newDate)
      })

      return {
        ...state,
        datePickerDates,
      }
    }

    case actions.GRID_DATE_PICKER_CONFIRM_DATES: {
      // save the dates in the date picker
      return {
        ...state,
        dates: state.datePickerDates.sortBy(date => date.date),
      }
    }

    case actions.GRID_REMOVE_DATE: {
      let { dates, datesToTimesToDeals, deals } = state

      // remove the date
      dates = dates.delete(action.dateId)

      // find removed deal ids
      let removedDealIds = new Set()
      datesToTimesToDeals.get(action.dateId).forEach(dealSet => {
        removedDealIds = removedDealIds.union(dealSet)
      })

      // check for any deals which have sold tickets for this date
      for (const dealId of removedDealIds) {
        if (deals.get(dealId).get('soldTickets') > 0) {
          return {
            ...state,
            operationError: 'This date has events which have sold tickets.',
          }
        }
      }

      // remove date from mapping
      datesToTimesToDeals = datesToTimesToDeals.delete(action.dateId)

      // set deleted flag on removed deals
      deals = deals.map(deal => (removedDealIds.has(deal.id) ? deal.set('deleted', true) : deal))

      return {
        ...state,
        dates,
        datesToTimesToDeals,
        deals,
      }
    }

    case actions.GRID_CREATE_TICKET: {
      return {
        ...state,
        ...createSellableItem(action.values)(state),
      }
    }

    case actions.GRID_EDIT_TICKET: {
      return {
        ...state,
        sellableItems: state.sellableItems.update(
          action.sellableItemId,
          sellableItem => sellableItem.merge(action.values).set('updated', true)
        ),
      }
    }

    case actions.GRID_REMOVE_TICKET: {
      const { sellableItemId } = action
      let { deals, datesToTimesToDeals } = state

      // Remove deals from datesToTimesToDeals and deals
      datesToTimesToDeals = datesToTimesToDeals.map(timesToDeals =>
        timesToDeals.map(dealSet =>
          dealSet.filterNot(dealId => deals.get(dealId).sellableItemId === sellableItemId)
        )
      )
      deals = deals.map(deal => (deal.sellableItemId === sellableItemId ? deal.set('deleted', true) : deal))

      return {
        ...state,
        ...recomputeGrid(),
        deals,
        datesToTimesToDeals,
        sellableItems: state.sellableItems.setIn([sellableItemId, 'deleted'], true),
      }
    }

    case actions.GRID_CREATE_DEAL: {
      const { dateId, timeId, sellableItemId } = action
      return {
        ...createDeal(dateId, timeId, sellableItemId, {})(state),
        ...recomputeGrid(),
      }
    }

    case actions.GRID_CREATE_TIME_DEALS: {
      const { timeId, sellableItemId } = action
      const { dates } = state

      let newState = state
      dates.forEach((_, dateId) => {
        newState = createDeal(dateId, timeId, sellableItemId, {})(newState)
      })

      return {
        ...newState,
        ...recomputeGrid(),
      }
    }

    case actions.GRID_CREATE_DATE_DEALS: {
      const { dateId, sellableItemId } = action
      const { times } = state

      let newState = state
      times.forEach((_, timeId) => {
        newState = createDeal(dateId, timeId, sellableItemId, {})(newState)
      })

      return {
        ...newState,
        ...recomputeGrid(),
      }
    }

    case actions.GRID_REMOVE_DEAL: {
      const { dealId, dateId, timeId } = action
      return {
        ...state,
        ...recomputeGrid(),
        deals: state.deals.setIn([dealId, 'deleted'], true),
        datesToTimesToDeals: state.datesToTimesToDeals.updateIn([dateId, timeId], dealSet => dealSet.delete(dealId)),
      }
    }

    case actions.GRID_CREATE_TIME: {
      const { hour, minute } = action
      const { updatedTimeState } = getOrCreateTime(hour, minute)(state)
      return updatedTimeState
    }

    case actions.GRID_REMOVE_TIME: {
      let { datesToTimesToDeals, deals, times } = state

      // remove the time
      times = times.delete(action.timeId)

      // find removed deal ids
      let removedDealIds = new Set()
      datesToTimesToDeals.forEach(timesToDeals => {
        removedDealIds = removedDealIds.union(timesToDeals.get(action.timeId, () => new Set()))
      })

      // check for any deals which have sold tickets for this time
      for (const dealId of removedDealIds) {
        if (deals.get(dealId).get('soldTickets') > 0) {
          return {
            ...state,
            operationError: 'This time has events which have sold tickets.',
          }
        }
      }

      // remove removed deals from mapping
      datesToTimesToDeals = datesToTimesToDeals.map(timesToDeals => timesToDeals.delete(action.timeId))

      // set deleted flag on removed deals
      deals = deals.map(deal => (removedDealIds.has(deal.id) ? deal.set('deleted', true) : deal))

      return {
        ...state,
        datesToTimesToDeals,
        deals,
        times,
      }
    }

    case actions.GRID_SAVE: {
      return {
        ...state,
        isSaving: true,
      }
    }

    case actions.GRID_SET_CURRENT_EDITING_SELLABLE_ITEM: {
      return {
        ...state,
        currentEditingSellableItemId: action.sellableItemId,
      }
    }

    case actions.GRID_SET_OPERATION_ERROR: {
      return {
        ...state,
        operationError: action.error,
      }
    }

    case actions.ENTITIES_CREATE_SELLABLE_ITEM.SUCCESS: {
      // Update serverId when creating sellable items
      return {
        ...state,
        sellableItems: state.sellableItems.setIn([action.args[0], 'serverId'], action.response.result),
      }
    }

    default:
      return state
  }
}



/** WEBPACK FOOTER **
 ** ./src/reducers/grid/grid.js
 **/